import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import StoryCarousel from '../components/StoryCarousel';
import { Loader2, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const setMeta = (name, content, isProperty = false) => {
  const attr = isProperty ? 'property' : 'name';
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const StoryPage = () => {
  const { storyId } = useParams();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const fetchStory = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await fetch(`${BACKEND_URL}/api/stories/detail/${storyId}`);
        if (!res.ok) throw new Error(res.status === 404 ? 'Story not found' : 'Failed to load story');
        const data = await res.json();
        if (cancelled) return;
        setStory(data);
        // Update tags for social preview scrapers that execute JS
        const title = `${data.primary_headline} — Clarity News`;
        document.title = title;
        setMeta('og:title', title, true);
        setMeta('og:description', `${data.article_count} sources on this story. See how each newsroom framed it on Clarity News.`, true);
        setMeta('og:type', 'article', true);
        setMeta('og:url', window.location.href, true);
        setMeta('twitter:card', 'summary_large_image');
        setMeta('twitter:title', title);
        setMeta('description', `${data.article_count} sources on this story. See how each newsroom framed it on Clarity News.`);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchStory();
    return () => {
      cancelled = true;
      // reset title + OG tags on unmount so they don't leak into other routes
      document.title = 'Clarity News — Break the Bubble';
      setMeta('og:title', 'Clarity News — Break the Bubble', true);
      setMeta('og:description', 'One story, every angle.', true);
      setMeta('og:url', window.location.origin, true);
      setMeta('twitter:title', 'Clarity News — Break the Bubble');
      setMeta('description', 'One story, every angle.');
    };
  }, [storyId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="story-page-loading">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error || !story) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="story-page-error">
        <h1 className="text-2xl font-serif font-bold text-gray-900 mb-3">This story is no longer here</h1>
        <p className="text-gray-600 mb-6">{error || 'It may have been merged, expired, or removed.'}</p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
          data-testid="story-page-back-home"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Clarity
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8" data-testid="story-page">
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-blue-600 mb-4 sm:mb-6"
        data-testid="story-page-back-link"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to feed
      </Link>
      <StoryCarousel story={story} />
    </div>
  );
};

export default StoryPage;
